/**
 * ABAC (Attribute-Based Access Control) scope helpers.
 *
 * Scope answers the question: "Can this membership act on this specific
 * tenant/hotel instance?" RBAC answers "Is the action allowed for this role?"
 * Both must pass:
 *
 *   allowed = hasPermission(role, permission) && hasScope(membership, scope)
 *
 * See hostesa-backend/docs/frontend/02-rbac-and-conventions.md for the rules.
 */

import type { Membership } from '@hostesa/api-client';

/**
 * The tenant/hotel context of the resource being accessed.
 * Always use resolved UUIDs (not slugs) for comparisons.
 */
export interface Scope {
  tenantId: string;
  hotelId?: string | null;
}

/**
 * Returns true when {@link membership} grants access to the given {@link scope}.
 *
 * Rules (from the backend RBAC doc):
 * - A tenant-wide membership (`hotelId: null`) matches any hotel under its
 *   tenant. The scope's `hotelId` is ignored — being in the tenant is enough.
 * - A hotel-scoped membership matches only when both `tenantId` AND `hotelId`
 *   match exactly.
 * - A membership for a different tenant NEVER matches, regardless of hotel.
 *
 * Cross-tenant access (e.g., `hostesa_admin`) is granted exclusively through
 * the `admin:cross_tenant_read` permission, not through membership scope. When
 * checking backoffice routes, the caller must also verify `hasPermission(role,
 * 'admin:cross_tenant_read')` separately.
 */
export function hasScope(membership: Membership, scope: Scope): boolean {
  // Tenant must always match — never reveal cross-tenant existence.
  if (membership.tenantId !== scope.tenantId) {
    return false;
  }

  // Tenant-wide membership: covers all hotels under this tenant.
  if (membership.hotelId === null) {
    return true;
  }

  // Hotel-scoped membership: must match the specific hotel being accessed.
  // If scope has no hotelId (pure tenant-level resource), a hotel-scoped
  // membership does NOT grant access to tenant-level operations.
  if (!scope.hotelId) {
    return false;
  }

  return membership.hotelId === scope.hotelId;
}

/**
 * Returns true when at least one membership in {@link memberships} grants
 * access to {@link scope}. Useful when a user has multiple memberships and
 * we want to know if any of them covers a given resource.
 */
export function hasAnyScope(memberships: readonly Membership[], scope: Scope): boolean {
  return memberships.some((m) => hasScope(m, scope));
}
